class BaseAPI {
    constructor(options = {}) {
        this.cache = new Map();
        this.cacheExpiration = options.cacheExpiration || 5 * 60 * 1000; // 5 minutes in milliseconds
        this.cacheKeyPattern = options.cacheKeyPattern || "${url}-${method}-${body}";
        this.baseUrl = options.baseUrl || '';
        this.debug = options.debug || false;
    }

    /**
     * Enable or disable debug mode
     * @param {boolean} enabled - Whether to enable debug mode
     */
    setDebug(enabled) {
        this.debug = enabled;
    }

    /**
     * Log debug message if debug mode is enabled
     * @param {string} message - Debug message
     * @param {any} data - Optional data to log
     */
    debugLog(message, data = null) {
        if (this.debug) {
            console.log(`[BaseAPI Debug] ${message}`, data || '');
        }
    }

    /**
     * Generate cache key based on pattern
     * @param {string} url - API endpoint URL
     * @param {Object} options - Fetch options
     * @returns {string} Generated cache key
     */
    generateCacheKey(url, options = {}) {
        const method = options.method || 'GET';
        const body = options.body ? JSON.stringify(options.body) : '';
        
        const key = this.cacheKeyPattern
            .replace('${url}', url)
            .replace('${method}', method)
            .replace('${body}', body)
            .replace('${baseUrl}', this.baseUrl);
        
        this.debugLog('Generated cache key:', key);
        return key;
    }

    /**
     * Get cached data if available and not expired
     * @param {string} key - Cache key
     * @returns {any|null} Cached data or null if not found/expired
     */
    getFromCache(key) {
        const cachedData = this.cache.get(key);
        if (!cachedData) {
            this.debugLog('Cache miss:', key);
            return null;
        }

        const { data, timestamp } = cachedData;
        if (Date.now() - timestamp > this.cacheExpiration) {
            this.debugLog('Cache expired:', key);
            this.cache.delete(key);
            return null;
        }

        this.debugLog('Cache hit:', { key, age: Date.now() - timestamp });
        return data;
    }

    /**
     * Store data in cache
     * @param {string} key - Cache key
     * @param {any} data - Data to cache
     */
    setCache(key, data) {
        this.cache.set(key, {
            data,
            timestamp: Date.now()
        });
        this.debugLog('Data cached:', { key, dataSize: JSON.stringify(data).length });
    }

    /**
     * Make an API request with caching and 429 handling
     * @param {string} url - API endpoint URL
     * @param {Object} options - Fetch options
     * @returns {Promise<any>} API response data
     */
    async request(url, options = {}) {
        const fullUrl = this.baseUrl ? `${this.baseUrl}${url}` : url;
        const cacheKey = this.generateCacheKey(url, options);
        const cachedData = this.getFromCache(cacheKey);

        try {
            // if cached data is available, return it
            if (cachedData) {
                this.debugLog('Returning cached data for:', fullUrl);
                return cachedData;
            }

            this.debugLog('Making API request:', fullUrl);
            // make the request
            const response = await fetch(fullUrl, options);

            if (response.status === 429) {
                this.debugLog('Rate limit hit:', fullUrl);
                if (cachedData) {
                    this.debugLog('Returning cached data due to rate limit');
                    return cachedData;
                }
                throw new Error('Rate limit exceeded and no cached data available');
            }

            if (!response.ok) {
                this.debugLog('Request failed:', { url: fullUrl, status: response.status });
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            this.setCache(cacheKey, data);
            this.debugLog('Request successful:', { url: fullUrl, dataSize: JSON.stringify(data).length });
            return data;
        } catch (error) {
            if (cachedData) {
                this.debugLog('Error occurred, returning cached data:', error.message);
                return cachedData;
            }
            this.debugLog('Error occurred, no cache available:', error.message);
            throw error;
        }
    }

    /**
     * Get cache statistics
     * @returns {Object} Cache statistics
     */
    getCacheStats() {
        const now = Date.now();
        const stats = {
            totalEntries: this.cache.size,
            expiredEntries: 0,
            validEntries: 0,
            oldestEntry: null,
            newestEntry: null
        };

        this.cache.forEach((value, key) => {
            const age = now - value.timestamp;
            if (age > this.cacheExpiration) {
                stats.expiredEntries++;
            } else {
                stats.validEntries++;
            }

            if (!stats.oldestEntry || value.timestamp < stats.oldestEntry.timestamp) {
                stats.oldestEntry = { key, timestamp: value.timestamp };
            }
            if (!stats.newestEntry || value.timestamp > stats.newestEntry.timestamp) {
                stats.newestEntry = { key, timestamp: value.timestamp };
            }
        });

        return stats;
    }

    /**
     * Set cache expiration time
     * @param {number} minutes - Cache expiration time in minutes
     */
    setCacheExpiration(minutes) {
        this.cacheExpiration = minutes * 60 * 1000;
    }

    /**
     * Set cache key pattern
     * @param {string} pattern - Cache key pattern
     */
    setCacheKeyPattern(pattern) {
        this.cacheKeyPattern = pattern;
    }

    /**
     * Set base URL for all requests
     * @param {string} baseUrl - Base URL
     */
    setBaseUrl(baseUrl) {
        this.baseUrl = baseUrl;
    }

    /**
     * Clear all cached data
     */
    clearCache() {
        this.cache.clear();
    }
}

export default BaseAPI;
